import React, { useState } from 'react';
import {
  Container,
  Grid,
  Paper,
  Typography,
  Card,
  CardContent,
  Button,
  Box,
  ThemeProvider,
  createTheme,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Avatar,
  Snackbar,
  Alert,
} from '@mui/material';
import {
  QrCode2,
  Forest,
  RecyclingRounded,
  TrendingUp,
  Share,
  Contactless,
  CreditCard,
} from '@mui/icons-material';
import './Home.css';

const theme = createTheme({
  palette: {
    primary: {
      main: '#4CAF50',
    },
    secondary: {
      main: '#81c784',
    },
  },
});

function Home() {
  const [openSnackbar, setOpenSnackbar] = useState(false);

  const handleGetCard = () => {
    setOpenSnackbar(true);
  };

  const handleCloseSnackbar = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setOpenSnackbar(false);
  };

  const cardFeatures = [
    { 
      title: 'Paper Saved', 
      value: '1000+ Cards', 
      icon: <Forest />, 
      trend: 'Saving trees daily' 
    },
    { 
      title: 'Digital Shares', 
      value: '24/7 Access', 
      icon: <Share />, 
      trend: 'Instant sharing' 
    },
    { 
      title: 'Carbon Reduced', 
      value: '90% Less', 
      icon: <RecyclingRounded />, 
      trend: 'vs traditional cards' 
    },
  ];

  const smartFeatures = [
    { 
      type: 'Instant Share',
      description: 'Share your card with a single tap',
      points: 'NFC Technology',
      icon: <Contactless />,
      date: 'Real-time updates'
    },
    {
      type: 'Smart QR Code',
      description: 'Scan to view digital profile',
      points: 'Dynamic QR',
      icon: <QrCode2 />,
      date: 'Always current'
    },
  ];

  const ecoFeatures = [
    {
      title: 'Sustainable Materials',
      description: 'Made from recycled materials and biodegradable components.',
      points: 'Eco-friendly'
    },
    {
      title: 'Digital First',
      description: 'Reduce paper waste with our smart digital business cards.',
      points: 'Zero waste'
    },
    {
      title: 'Update Anytime',
      description: 'Change your info instantly without reprinting cards.',
      points: 'Cost-effective'
    }
  ];

  return (
    <ThemeProvider theme={theme}>
      <div className="home-container">
        <Container maxWidth="lg">
          {/* Hero Section */}
          <Box className="welcome-section" textAlign="center" py={8}>
            <Typography variant="h3" component="h1" gutterBottom>
              Smart EcoCard
            </Typography>
            <Typography variant="h5" color="textSecondary" gutterBottom>
              The Future of Networking is Sustainable
            </Typography>
            <Typography variant="subtitle1" color="textSecondary" paragraph>
              Transform your professional networking with our smart, eco-friendly digital business cards.
              Share your contact information instantly while helping the environment.
            </Typography>
            <Button
              variant="contained"
              color="primary"
              size="large"
              startIcon={<CreditCard />}
              sx={{ mt: 4 }}
              onClick={handleGetCard}
            >
              Get Your Smart EcoCard
            </Button>
          </Box>

          {/* Feature Cards */}
          <Grid container spacing={3} className="stats-section">
            {cardFeatures.map((feature, index) => (
              <Grid item xs={12} sm={4} key={index}>
                <Paper elevation={3} className="stat-paper">
                  <Box className="stat-icon-container">
                    {feature.icon}
                  </Box>
                  <Typography variant="h6" className="stat-title">
                    {feature.title}
                  </Typography>
                  <Typography variant="h4" className="stat-value">
                    {feature.value}
                  </Typography>
                  <Box className="stat-trend">
                    <TrendingUp fontSize="small" />
                    <Typography variant="body2" color="textSecondary">
                      {feature.trend}
                    </Typography>
                  </Box>
                </Paper>
              </Grid>
            ))}
          </Grid>

          <Grid container spacing={3} className="content-section">
            {/* Smart Features */}
            <Grid item xs={12} md={6}>
              <Paper elevation={3} className="section-paper">
                <Typography variant="h5" className="section-title" gutterBottom>
                  Smart Features
                </Typography>
                <List>
                  {smartFeatures.map((feature, index) => (
                    <ListItem key={index} className="activity-item">
                      <ListItemIcon className="activity-icon">
                        <Avatar className="eco-avatar">
                          {feature.icon}
                        </Avatar>
                      </ListItemIcon>
                      <ListItemText
                        primary={feature.type}
                        secondary={
                          <React.Fragment>
                            <Typography component="span" variant="body2" color="textPrimary">
                              {feature.description}
                            </Typography>
                            <br />
                            <Typography component="span" variant="caption" color="textSecondary">
                              {feature.date}
                            </Typography>
                          </React.Fragment>
                        }
                      />
                      <Typography variant="subtitle2" color="primary" className="feature-tag">
                        {feature.points}
                      </Typography>
                    </ListItem>
                  ))}
                </List>
              </Paper>
            </Grid>

            {/* Eco Benefits */}
            <Grid item xs={12} md={6}>
              <Paper elevation={3} className="section-paper">
                <Typography variant="h5" className="section-title" gutterBottom>
                  Eco Benefits
                </Typography>
                <Grid container spacing={2}>
                  {ecoFeatures.map((feature, index) => (
                    <Grid item xs={12} key={index}>
                      <Card className="feature-card">
                        <CardContent>
                          <Typography variant="h6" gutterBottom>
                            {feature.title}
                          </Typography>
                          <Typography variant="body2" color="textSecondary">
                            {feature.description}
                          </Typography>
                          <Box className="eco-tag" mt={1}>
                            <RecyclingRounded color="primary" fontSize="small" />
                            <Typography variant="body2" color="primary">
                              {feature.points}
                            </Typography>
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </div>
      <Snackbar
        open={openSnackbar}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity="info" variant="filled" sx={{ width: '100%' }}>
          Coming Soon! We're working hard to bring you the future of eco-friendly business cards.
        </Alert>
      </Snackbar>
    </ThemeProvider>
  );
}

export default Home;
